import React, { useCallback, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import 'moment/locale/pt-br';
import { Popconfirm, Button, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import { dateFormat } from '@utils/formatters';
import SelectCustomers from '@components/SelectCustomers';
import ModalSender from './ModalSender';

const SmsSenders = () => {
  const { t } = useTranslation('sms-send');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['customerId'],
  });

  const { hasCustomersRoles } = useContext(SessionContext);

  const { customerId } = useMemo(() => searchParams, [searchParams]);

  const { data, mutate } = useSwr(`/service-proxy/sms-senders`, {
    params: {
      customerId,
    },
  });

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles,
        label: t('table.customerName'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
    ],
    [t, customerId, setSearchParams, hasCustomersRoles],
  );

  const handleDelete = useCallback(
    async (idToDelete: string) => {
      try {
        await api.delete(`/service-proxy/sms-senders/${idToDelete}`);
        message.success(t('sender.deleted'));
      } catch (err) {
        message.error(t('sender.error'));
      }
      mutate();
    },
    [mutate, t],
  );

  const columns = useMemo(() => {
    const defaultColumns = [
      { title: t('table.customerName'), dataIndex: 'customerName' },
      { title: t('table.sender'), dataIndex: 'sender' },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.updatedAt'),
        dataIndex: 'updatedAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('table.updatedBy'),
        dataIndex: 'updatedBy',
      },
      {
        title: t('columns.actions'),
        dataIndex: 'actions',
        render: (_: any, record: any) => (
          <>
            <ModalSender defaultValues={record} mutate={mutate} />

            <Popconfirm
              title={t('pop-over.confirm-delete')}
              cancelText={t('pop-over.no')}
              okText={t('pop-over.yes')}
              onConfirm={() => handleDelete(record.id)}
            >
              <Button size="large" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </>
        ),
      },
    ];

    return defaultColumns;
  }, [t, mutate, handleDelete]);

  return (
    <>
      <Table
        title={t('title.sender')}
        columns={columns}
        data={data}
        extraActions={
          <>
            <ModalSender mutate={mutate} />
          </>
        }
        filters={{
          list: tableFilters,
          openByDefault: true,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        loading={!data}
        tableKey="sms-send-senders"
      />
    </>
  );
};

export default SmsSenders;
